import { createElement, useEffect, useRef, useState, type ReactNode } from 'react';

import { isOnline } from '@/lib/connectivity';
import type { DatabaseColumnDefinition } from '@/lib/databases/types';
import type {
  DatabaseFilterClause,
  DatabaseSortClause,
  DatabaseSortField,
} from '@/lib/databases/build-query';
import { anchorBelowElement, Menu } from '@/components/ui/Menu.web';

import type { DatabaseFilterOperator } from '@couplespace/core/databases';
import { queryDatabase } from '@/lib/databases/api';
import { normalizeSelectOptions, relationTargetDatabaseId } from '@/lib/databases/values';
import { stopBubbleUnlessFormField } from '@/lib/databases/stop-editor-bubble';

import { AddColumnButton } from './ColumnMenu.web';
import { SelectFilterValuePicker } from './SelectFilterValuePicker.web';
import {
  IconFilter,
  IconGroup,
  IconMenu,
  IconSearch,
  IconSettings,
  IconSort,
} from './DatabaseMenuIcons.web';
import {
  coerceFilterValue,
  defaultOperatorForType,
  filterOperatorsForColumnType,
  operatorNeedsValue,
  TITLE_FILTER_FIELD,
} from './filter-operators';

type DatabaseToolbarProps = {
  columns: DatabaseColumnDefinition[];
  search: string;
  onSearchChange: (value: string) => void;
  filters: DatabaseFilterClause[];
  onFiltersChange: (filters: DatabaseFilterClause[]) => void;
  sorts: DatabaseSortClause[];
  onSortsChange: (sorts: DatabaseSortClause[]) => void;
  groupBy: DatabaseSortField | null;
  onGroupByChange: (groupBy: DatabaseSortField | null) => void;
  disabled?: boolean;
  onImportCsv?: () => void;
  onExport?: (format: 'csv' | 'json' | 'markdown') => void;
  exportBusy?: boolean;
  onAddColumn?: (name: string, type: string) => void;
  onBeforeAddColumn?: () => void;
  /** Table-only: column visibility and layout toggles. */
  tableLayout?: {
    columns: DatabaseColumnDefinition[];
    hiddenColumns?: string[];
    frozenTitle?: boolean;
    wrapText?: boolean;
    onHiddenColumnsChange: (hidden: string[]) => void;
    onFrozenTitleChange: (frozen: boolean) => void;
    onWrapTextChange: (wrap: boolean) => void;
  };
  /** Board-only: select column used as status lanes. */
  boardLayout?: {
    selectColumns: DatabaseColumnDefinition[];
    boardColumnId?: string;
    onBoardColumnChange: (columnId: string) => void;
  };
  /** Calendar-only: date column used for placement (supports ranges). */
  calendarLayout?: {
    dateColumns: DatabaseColumnDefinition[];
    calendarColumnId?: string;
    onCalendarColumnChange: (columnId: string) => void;
    colorColumns: DatabaseColumnDefinition[];
    colorColumnId?: string;
    onColorColumnChange: (columnId: string | undefined) => void;
  };
  /** Timeline-only: date column used for bar placement (supports ranges). */
  timelineLayout?: {
    dateColumns: DatabaseColumnDefinition[];
    timelineColumnId?: string;
    onTimelineColumnChange: (columnId: string) => void;
    colorColumns: DatabaseColumnDefinition[];
    colorColumnId?: string;
    onColorColumnChange: (columnId: string | undefined) => void;
  };
};

type PanelKind = 'filter' | 'sort' | 'group' | 'properties' | 'more' | null;

function ToolbarSearch({
  search,
  onSearchChange,
  disabled,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(() => {
    if (search.trim().length > 0) {
      return true;
    }
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 639px)').matches;
    }
    return false;
  });
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (search.trim().length > 0) {
      setOpen(true);
    }
  }, [search]);

  function focusSearch() {
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function tryClose() {
    if (!search.trim()) {
      setOpen(false);
    }
  }

  const isNarrowViewport =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
  const expanded = isNarrowViewport || open || search.trim().length > 0;

  return createElement(
    'div',
    {
      className: expanded
        ? 'cs-db-toolbar-search-wrap cs-db-toolbar-search-wrap-open cs-db-toolbar-search-wrap-expanded'
        : 'cs-db-toolbar-search-wrap',
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
    },
    createElement(
      'button',
      {
        type: 'button',
        className: expanded
          ? 'cs-db-toolbar-icon-btn cs-db-toolbar-icon-btn-active'
          : 'cs-db-toolbar-icon-btn',
        title: 'Search',
        'aria-label': 'Search',
        'aria-expanded': expanded,
        disabled,
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        onClick: (e: MouseEvent) => {
          e.stopPropagation();
          focusSearch();
        },
      },
      IconSearch(),
    ),
    createElement('input', {
      ref: inputRef,
      type: 'search',
      className: 'cs-db-toolbar-search',
      placeholder: 'Type to search…',
      value: search,
      disabled,
      'aria-hidden': !expanded,
      tabIndex: expanded ? 0 : -1,
      onChange: (e: Event) => onSearchChange((e.target as HTMLInputElement).value),
      onBlur: (e: FocusEvent) => {
        const next = e.relatedTarget as Node | null;
        if (next && (e.currentTarget as HTMLElement).parentElement?.contains(next)) {
          return;
        }
        tryClose();
      },
      onKeyDown: (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onSearchChange('');
          setOpen(false);
          (e.currentTarget as HTMLInputElement).blur();
        }
      },
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      onClick: (e: MouseEvent) => e.stopPropagation(),
    }),
  );
}

function toolbarIconButton(
  label: string,
  icon: ReactNode,
  active: boolean,
  onClick: (el: HTMLElement) => void,
  options?: { badge?: boolean },
) {
  const classNames = ['cs-db-toolbar-icon-btn'];
  if (active) {
    classNames.push('cs-db-toolbar-icon-btn-active');
  }
  if (options?.badge) {
    classNames.push('cs-db-toolbar-icon-btn-badge');
  }
  return createElement(
    'button',
    {
      type: 'button',
      className: classNames.join(' '),
      title: label,
      'aria-label': label,
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        onClick(e.currentTarget as HTMLElement);
      },
    },
    icon,
  );
}

function fieldTypeForId(
  fieldId: string,
  columns: DatabaseColumnDefinition[],
): string {
  if (fieldId === TITLE_FILTER_FIELD) {
    return TITLE_FILTER_FIELD;
  }
  return columns.find((c) => c.id === fieldId)?.type ?? 'text';
}

function RelationFilterValueSelect({
  targetDbId,
  value,
  disabled,
  onChange,
}: {
  targetDbId: string;
  value: unknown;
  disabled?: boolean;
  onChange: (rowId: string) => void;
}) {
  const [rows, setRows] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void queryDatabase(targetDbId, { limit: 100, includeProperties: false })
      .then((result) => {
        if (!cancelled) {
          setRows(
            result.rows.map((r) => ({
              id: r.id,
              title: r.title?.trim() || 'Untitled',
            })),
          );
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [targetDbId]);

  if (loading) {
    return createElement(
      'span',
      { className: 'cs-db-toolbar-hint cs-db-toolbar-value' },
      'Loading…',
    );
  }

  return createElement(
    'select',
    {
      className: 'cs-db-toolbar-select cs-db-toolbar-value',
      value: value === undefined || value === null ? '' : String(value),
      disabled,
      onChange: (e: Event) => onChange((e.target as HTMLSelectElement).value),
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
    },
    createElement('option', { value: '' }, 'Choose row…'),
    ...rows.map((row) => createElement('option', { key: row.id, value: row.id }, row.title)),
  );
}

function defaultFilterValueForType(type: string): unknown {
  if (type === 'checkbox') {
    return true;
  }
  if (type === 'select' || type === 'multi_select') {
    return [];
  }
  return '';
}

function normalizeSelectFilterValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }
  if (value === undefined || value === null || value === '') {
    return [];
  }
  return [String(value)];
}

function filtersEqual(a: DatabaseFilterClause[], b: DatabaseFilterClause[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function newFilterClause(columns: DatabaseColumnDefinition[]): DatabaseFilterClause {
  const fieldId = columns[0]?.id ?? TITLE_FILTER_FIELD;
  const type = fieldTypeForId(fieldId, columns);
  return {
    propertyId: fieldId === TITLE_FILTER_FIELD ? 'title' : fieldId,
    operator: defaultOperatorForType(type),
    value: defaultFilterValueForType(type),
  };
}

function newSortClause(): DatabaseSortClause {
  return { field: 'title', direction: 'asc' };
}

function FilterPanel({
  columns,
  filters,
  onDraftChange,
  onApply,
  hasPendingChanges,
}: {
  columns: DatabaseColumnDefinition[];
  filters: DatabaseFilterClause[];
  onDraftChange: (filters: DatabaseFilterClause[]) => void;
  onApply: () => void;
  hasPendingChanges: boolean;
}) {
  function updateFilter(index: number, patch: Partial<DatabaseFilterClause>) {
    const next = filters.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onDraftChange(next);
  }

  function onFieldChange(index: number, fieldId: string) {
    const type = fieldTypeForId(fieldId, columns);
    const operator = defaultOperatorForType(type);
    updateFilter(index, {
      propertyId: fieldId === TITLE_FILTER_FIELD ? 'title' : fieldId,
      operator,
      value: defaultFilterValueForType(type),
    });
  }

  return createElement(
    'div',
    { className: 'cs-db-toolbar-panel' },
    filters.length === 0
      ? createElement('p', { className: 'cs-db-toolbar-hint' }, 'No filters. Add one below.')
      : null,
    ...filters.map((clause, index) => {
      const fieldId =
        clause.propertyId === 'title' || !clause.propertyId
          ? TITLE_FILTER_FIELD
          : clause.propertyId;
      const colType = fieldTypeForId(fieldId, columns);
      const colDef =
        fieldId !== TITLE_FILTER_FIELD
          ? columns.find((c) => c.id === fieldId)
          : undefined;
      const selectOptions =
        colDef && (colDef.type === 'select' || colDef.type === 'multi_select')
          ? normalizeSelectOptions(colDef.config)
          : [];
      const relationTargetId =
        colDef?.type === 'relation' ? relationTargetDatabaseId(colDef.config) : null;
      const operators = filterOperatorsForColumnType(colType);
      const opDef = operators.find((o) => o.operator === clause.operator) ?? operators[0];
      const needsValue = opDef ? operatorNeedsValue(opDef.operator) : false;
      const useSelectPicker =
        needsValue &&
        opDef?.valueInput === 'select' &&
        (colDef?.type === 'select' || colDef?.type === 'multi_select');

      const controlsRow = createElement(
        'div',
        { className: 'cs-db-toolbar-row' },
        createElement(
          'select',
          {
            className: 'cs-db-toolbar-select',
            value: fieldId,
            onChange: (e: Event) =>
              onFieldChange(index, (e.target as HTMLSelectElement).value),
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          createElement('option', { value: TITLE_FILTER_FIELD }, 'Title'),
          ...columns.map((col) =>
            createElement('option', { key: col.id, value: col.id }, col.name),
          ),
        ),
        createElement(
          'select',
          {
            className: 'cs-db-toolbar-select',
            value: clause.operator,
            onChange: (e: Event) => {
              const operator = (e.target as HTMLSelectElement)
                .value as DatabaseFilterOperator;
              const nextOps = filterOperatorsForColumnType(colType);
              const nextDef = nextOps.find((o) => o.operator === operator);
              let value: unknown = clause.value;
              if (nextDef && !operatorNeedsValue(nextDef.operator)) {
                value = undefined;
              } else if (
                colDef &&
                (colDef.type === 'select' || colDef.type === 'multi_select')
              ) {
                value = normalizeSelectFilterValue(clause.value);
              }
              updateFilter(index, { operator, value });
            },
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          ...operators.map((o) =>
            createElement('option', { key: o.operator, value: o.operator }, o.label),
          ),
        ),
        !useSelectPicker && needsValue && opDef?.valueInput === 'checkbox'
          ? createElement(
              'select',
              {
                className: 'cs-db-toolbar-select cs-db-toolbar-value',
                value: clause.value === true || clause.value === 'true' ? 'true' : 'false',
                onChange: (e: Event) =>
                  updateFilter(index, {
                    value: (e.target as HTMLSelectElement).value === 'true',
                  }),
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
              },
              createElement('option', { value: 'true' }, 'Checked'),
              createElement('option', { value: 'false' }, 'Unchecked'),
            )
          : !useSelectPicker && needsValue && colDef?.type === 'relation' && relationTargetId
            ? createElement(RelationFilterValueSelect, {
                targetDbId: relationTargetId,
                value: clause.value,
                disabled: false,
                onChange: (rowId) => updateFilter(index, { value: rowId }),
              })
          : !useSelectPicker && needsValue
            ? createElement('input', {
                type: opDef?.valueInput === 'number' ? 'number' : 'text',
                className: 'cs-db-toolbar-input cs-db-toolbar-value',
                value:
                  clause.value === undefined || clause.value === null
                    ? ''
                    : String(clause.value),
                onChange: (e: Event) => {
                  const raw = (e.target as HTMLInputElement).value;
                  updateFilter(index, {
                    value: coerceFilterValue(
                      colType,
                      clause.operator as DatabaseFilterOperator,
                      raw,
                    ),
                  });
                },
                onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                onClick: (e: MouseEvent) => e.stopPropagation(),
              })
            : !useSelectPicker
              ? createElement('span', { className: 'cs-db-toolbar-value-spacer' })
              : null,
        createElement(
          'button',
          {
            type: 'button',
            className: 'cs-db-toolbar-icon-btn',
            title: 'Remove filter',
            onClick: () => onDraftChange(filters.filter((_, i) => i !== index)),
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          '×',
        ),
      );

      return createElement(
        'div',
        { key: `filter-${index}`, className: 'cs-db-toolbar-filter-block' },
        controlsRow,
        useSelectPicker
          ? createElement(SelectFilterValuePicker, {
              options: selectOptions,
              value: clause.value,
              onChange: (ids) => updateFilter(index, { value: ids }),
            })
          : null,
      );
    }),
    createElement(
      'div',
      { className: 'cs-db-toolbar-panel-actions cs-db-toolbar-panel-actions-filter' },
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-toolbar-link',
          onClick: () => onDraftChange([...filters, newFilterClause(columns)]),
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        'Add filter',
      ),
      filters.length > 0
        ? createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-toolbar-link cs-db-toolbar-link-muted',
              onClick: () => onDraftChange([]),
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
            },
            'Clear all',
          )
        : null,
      createElement(
        'button',
        {
          type: 'button',
          className: hasPendingChanges
            ? 'cs-db-toolbar-link cs-db-toolbar-link-end'
            : 'cs-db-toolbar-link cs-db-toolbar-link-muted cs-db-toolbar-link-end',
          disabled: !hasPendingChanges,
          onClick: (e: MouseEvent) => {
            e.stopPropagation();
            onApply();
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        'Apply',
      ),
    ),
  );
}

function SortPanel({
  columns,
  sorts,
  onSortsChange,
}: {
  columns: DatabaseColumnDefinition[];
  sorts: DatabaseSortClause[];
  onSortsChange: (sorts: DatabaseSortClause[]) => void;
}) {
  function parseFieldValue(value: string): DatabaseSortField {
    if (value === 'title' || value === 'position') {
      return value;
    }
    return { propertyId: value };
  }

  function fieldValue(field: DatabaseSortField): string {
    if (field === 'title' || field === 'position') {
      return field;
    }
    return field.propertyId;
  }

  return createElement(
    'div',
    { className: 'cs-db-toolbar-panel' },
    sorts.length === 0
      ? createElement('p', { className: 'cs-db-toolbar-hint' }, 'Default sort: position ascending.')
      : null,
    ...sorts.map((clause, index) =>
      createElement(
        'div',
        { key: `sort-${index}`, className: 'cs-db-toolbar-row' },
        createElement(
          'select',
          {
            className: 'cs-db-toolbar-select',
            value: fieldValue(clause.field),
            onChange: (e: Event) =>
              onSortsChange(
                sorts.map((s, i) =>
                  i === index
                    ? { ...s, field: parseFieldValue((e.target as HTMLSelectElement).value) }
                    : s,
                ),
              ),
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          createElement('option', { value: 'title' }, 'Title'),
          createElement('option', { value: 'position' }, 'Position'),
          ...columns.map((col) =>
            createElement('option', { key: col.id, value: col.id }, col.name),
          ),
        ),
        createElement(
          'button',
          {
            type: 'button',
            className: 'cs-db-toolbar-select cs-db-toolbar-direction',
            onClick: () =>
              onSortsChange(
                sorts.map((s, i) =>
                  i === index
                    ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }
                    : s,
                ),
              ),
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          clause.direction === 'asc' ? 'Ascending' : 'Descending',
        ),
        createElement(
          'button',
          {
            type: 'button',
            className: 'cs-db-toolbar-icon-btn',
            title: 'Remove sort',
            onClick: () => onSortsChange(sorts.filter((_, i) => i !== index)),
            onMouseDown: (e: MouseEvent) => e.stopPropagation(),
          },
          '×',
        ),
      ),
    ),
    createElement(
      'div',
      { className: 'cs-db-toolbar-panel-actions' },
      createElement(
        'button',
        {
          type: 'button',
          className: 'cs-db-toolbar-link',
          onClick: () => onSortsChange([...sorts, newSortClause()]),
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        'Add sort',
      ),
      sorts.length > 0
        ? createElement(
            'button',
            {
              type: 'button',
              className: 'cs-db-toolbar-link cs-db-toolbar-link-muted',
              onClick: () => onSortsChange([]),
              onMouseDown: (e: MouseEvent) => e.stopPropagation(),
            },
            'Reset to default',
          )
        : null,
    ),
  );
}

function GroupPanel({
  columns,
  groupBy,
  onGroupByChange,
}: {
  columns: DatabaseColumnDefinition[];
  groupBy: DatabaseSortField | null;
  onGroupByChange: (groupBy: DatabaseSortField | null) => void;
}) {
  const value =
    groupBy === null
      ? ''
      : groupBy === 'title' || groupBy === 'position'
        ? groupBy
        : groupBy.propertyId;

  return createElement(
    'div',
    { className: 'cs-db-toolbar-panel cs-db-toolbar-panel-narrow' },
    createElement(
      'label',
      { className: 'cs-db-toolbar-label' },
      'Group by',
      createElement(
        'select',
        {
          className: 'cs-db-toolbar-select cs-db-toolbar-select-full',
          value,
          onChange: (e: Event) => {
            const v = (e.target as HTMLSelectElement).value;
            if (!v) {
              onGroupByChange(null);
            } else if (v === 'title' || v === 'position') {
              onGroupByChange(v);
            } else {
              onGroupByChange({ propertyId: v });
            }
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        createElement('option', { value: '' }, 'None'),
        createElement('option', { value: 'title' }, 'Title'),
        createElement('option', { value: 'position' }, 'Position'),
        ...columns.map((col) =>
          createElement('option', { key: col.id, value: col.id }, col.name),
        ),
      ),
    ),
  );
}

function propertiesCheckboxRow(
  label: string,
  checked: boolean,
  onChange: (checked: boolean) => void,
) {
  return createElement(
    'label',
    { className: 'cs-db-toolbar-prop-row' },
    createElement('input', {
      type: 'checkbox',
      checked,
      onChange: (e: Event) => onChange((e.target as HTMLInputElement).checked),
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
    }),
    createElement('span', { className: 'cs-db-toolbar-prop-label' }, label),
  );
}

function DateColumnSettingsPanel({
  dateColumns,
  columnId,
  onColumnChange,
  hint,
  colorColumns,
  colorColumnId,
  onColorColumnChange,
}: {
  dateColumns: DatabaseColumnDefinition[];
  columnId?: string;
  onColumnChange: (columnId: string) => void;
  hint: string;
  colorColumns: DatabaseColumnDefinition[];
  colorColumnId?: string;
  onColorColumnChange: (columnId: string | undefined) => void;
}) {
  return createElement(
    'div',
    { className: 'cs-db-toolbar-panel cs-db-toolbar-panel-narrow' },
    createElement(
      'label',
      { className: 'cs-db-toolbar-label' },
      'Date column',
      createElement(
        'select',
        {
          className: 'cs-db-toolbar-select cs-db-toolbar-select-full',
          value: columnId ?? '',
          onChange: (e: Event) => {
            const id = (e.target as HTMLSelectElement).value;
            if (id) {
              onColumnChange(id);
            }
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        columnId ? null : createElement('option', { value: '' }, 'Choose a column…'),
        ...dateColumns.map((col) =>
          createElement('option', { key: col.id, value: col.id }, col.name),
        ),
      ),
    ),
    createElement('p', { className: 'cs-db-toolbar-hint' }, hint),
    createElement(
      'label',
      { className: 'cs-db-toolbar-label cs-db-toolbar-label-gap' },
      'Color',
      createElement(
        'select',
        {
          className: 'cs-db-toolbar-select cs-db-toolbar-select-full',
          value: colorColumnId ?? '',
          onChange: (e: Event) => {
            const id = (e.target as HTMLSelectElement).value;
            onColorColumnChange(id || undefined);
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        createElement('option', { value: '' }, 'None'),
        ...colorColumns.map((col) =>
          createElement('option', { key: col.id, value: col.id }, col.name),
        ),
      ),
    ),
    createElement(
      'p',
      { className: 'cs-db-toolbar-hint' },
      colorColumns.length > 0
        ? 'Colors each item with the option chosen in this column.'
        : 'Add a select column to color items.',
    ),
  );
}

function BoardSettingsPanel({
  selectColumns,
  boardColumnId,
  onBoardColumnChange,
}: NonNullable<DatabaseToolbarProps['boardLayout']>) {
  return createElement(
    'div',
    { className: 'cs-db-toolbar-panel cs-db-toolbar-panel-narrow' },
    createElement(
      'label',
      { className: 'cs-db-toolbar-label' },
      'Status column',
      createElement(
        'select',
        {
          className: 'cs-db-toolbar-select cs-db-toolbar-select-full',
          value: boardColumnId ?? '',
          onChange: (e: Event) => {
            const id = (e.target as HTMLSelectElement).value;
            if (id) {
              onBoardColumnChange(id);
            }
          },
          onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        },
        boardColumnId
          ? null
          : createElement('option', { value: '' }, 'Choose a column…'),
        ...selectColumns.map((col) =>
          createElement('option', { key: col.id, value: col.id }, col.name),
        ),
      ),
    ),
  );
}

function PropertiesPanel({
  columns,
  hiddenColumns,
  frozenTitle,
  wrapText,
  onHiddenColumnsChange,
  onFrozenTitleChange,
  onWrapTextChange,
}: NonNullable<DatabaseToolbarProps['tableLayout']>) {
  const hidden = new Set(hiddenColumns ?? []);
  return createElement(
    'div',
    { className: 'cs-db-toolbar-properties-panel' },
    createElement(
      'div',
      { className: 'cs-db-toolbar-properties-section' },
      propertiesCheckboxRow('Wrap text', wrapText === true, onWrapTextChange),
      propertiesCheckboxRow('Freeze title column', frozenTitle === true, onFrozenTitleChange),
    ),
    createElement('hr', { className: 'cs-db-toolbar-properties-divider', 'aria-hidden': true }),
    createElement(
      'div',
      { className: 'cs-db-toolbar-properties-section' },
      createElement('p', { className: 'cs-db-toolbar-properties-heading' }, 'Visible columns'),
      createElement(
        'div',
        { className: 'cs-db-toolbar-properties-columns' },
        columns.length === 0
          ? createElement('p', { className: 'cs-db-toolbar-properties-empty' }, 'No columns yet')
          : columns.map((col) =>
              createElement(
                'label',
                { key: col.id, className: 'cs-db-toolbar-prop-row' },
                createElement('input', {
                  type: 'checkbox',
                  checked: !hidden.has(col.id),
                  onChange: (e: Event) => {
                    const show = (e.target as HTMLInputElement).checked;
                    const next = show
                      ? (hiddenColumns ?? []).filter((id) => id !== col.id)
                      : [...(hiddenColumns ?? []), col.id];
                    onHiddenColumnsChange(next);
                  },
                  onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                }),
                createElement('span', { className: 'cs-db-toolbar-prop-label' }, col.name),
              ),
            ),
      ),
    ),
  );
}

function MorePanel({
  onImportCsv,
  onExport,
  onClose,
  disabled,
  exportBusy,
}: {
  onImportCsv?: () => void;
  onExport?: (format: 'csv' | 'json' | 'markdown') => void;
  onClose: () => void;
  disabled?: boolean;
  exportBusy?: boolean;
}) {
  const exportItems: { label: string; format: 'csv' | 'json' | 'markdown' }[] = [
    { label: 'CSV', format: 'csv' },
    { label: 'JSON', format: 'json' },
    { label: 'Markdown', format: 'markdown' },
  ];
  return createElement(
    'div',
    { className: 'cs-db-toolbar-panel cs-db-toolbar-more-panel' },
    onImportCsv
      ? createElement(
          'button',
          {
            type: 'button',
            className: 'cs-db-toolbar-more-item',
            disabled,
            onClick: () => {
              onImportCsv();
              onClose();
            },
          },
          'Import CSV',
        )
      : null,
    onExport
      ? createElement(
          'div',
          { className: 'cs-db-toolbar-more-section' },
          createElement('div', { className: 'cs-db-toolbar-more-heading' }, 'Export'),
          ...exportItems.map((item) =>
            createElement(
              'button',
              {
                key: item.format,
                type: 'button',
                className: 'cs-db-toolbar-more-item cs-db-toolbar-more-item-nested',
                disabled: disabled || exportBusy,
                onClick: () => {
                  onExport(item.format);
                  onClose();
                },
              },
              exportBusy ? `${item.label}…` : item.label,
            ),
          ),
        )
      : null,
  );
}

function toolbarMenuButton(
  title: string,
  active: boolean,
  disabled: boolean | undefined,
  onClick: (el: HTMLElement) => void,
  icon: ReactNode,
) {
  return createElement(
    'button',
    {
      type: 'button',
      className: active
        ? 'cs-db-toolbar-add-btn cs-db-toolbar-add-btn-active'
        : 'cs-db-toolbar-add-btn',
      title,
      'aria-label': title,
      disabled,
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        onClick(e.currentTarget as HTMLElement);
      },
    },
    createElement('span', { className: 'cs-db-toolbar-menu-btn-icon' }, icon),
  );
}

export function DatabaseToolbar({
  columns,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  sorts,
  onSortsChange,
  groupBy,
  onGroupByChange,
  disabled,
  onImportCsv,
  onExport,
  exportBusy,
  onAddColumn,
  onBeforeAddColumn,
  tableLayout,
  boardLayout,
  calendarLayout,
  timelineLayout,
}: DatabaseToolbarProps) {
  const [networkOnline, setNetworkOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  useEffect(() => {
    const sync = () => setNetworkOnline(isOnline());
    sync();
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  const importExportDisabled = disabled || !networkOnline;

  const [panel, setPanel] = useState<PanelKind>(null);
  const [anchor, setAnchor] = useState<ReturnType<typeof anchorBelowElement> | null>(null);
  const [draftFilters, setDraftFilters] = useState(filters);

  useEffect(() => {
    if (panel !== 'filter') {
      setDraftFilters(filters);
    }
  }, [filters, panel]);

  const filterPendingChanges = panel === 'filter' && !filtersEqual(draftFilters, filters);

  function applyFilters() {
    onFiltersChange(draftFilters);
    setPanel(null);
    setAnchor(null);
  }

  function openPanel(kind: PanelKind, el: HTMLElement) {
    if (panel === kind) {
      setPanel(null);
      setAnchor(null);
      return;
    }
    if (kind === 'filter') {
      setDraftFilters(filters);
    }
    setPanel(kind);
    setAnchor(anchorBelowElement(el));
  }

  function closePanel() {
    setPanel(null);
    setAnchor(null);
  }

  const filterCount = filters.length;
  const sortCount = sorts.length;
  const groupActive = groupBy !== null;

  const filterLabel =
    filterCount > 0 ? `Filter (${filterCount})` : 'Filter';
  const sortLabel = sortCount > 0 ? `Sort (${sortCount})` : 'Sort';

  return createElement(
    'div',
    {
      className: 'cs-db-toolbar',
      onMouseDown: (e: MouseEvent) => e.stopPropagation(),
      onKeyDownCapture: stopBubbleUnlessFormField,
    },
    createElement(
      'div',
      { className: 'cs-db-toolbar-actions' },
      createElement(ToolbarSearch, { search, onSearchChange, disabled }),
      toolbarIconButton(
        filterLabel,
        IconFilter(),
        panel === 'filter' || filterCount > 0,
        (el) => openPanel('filter', el),
        { badge: filterCount > 0 && panel !== 'filter' },
      ),
      toolbarIconButton(
        sortLabel,
        IconSort(),
        panel === 'sort' || sortCount > 0,
        (el) => openPanel('sort', el),
        { badge: sortCount > 0 && panel !== 'sort' },
      ),
      toolbarIconButton(
        groupActive ? 'Grouped' : 'Group',
        IconGroup(),
        panel === 'group' || groupActive,
        (el) => openPanel('group', el),
      ),
      tableLayout || boardLayout || calendarLayout || timelineLayout
        ? toolbarIconButton('View settings', IconSettings(), panel === 'properties', (el) =>
            openPanel('properties', el),
          )
        : null,
      onImportCsv || onExport
        ? toolbarMenuButton(
            'Database actions',
            panel === 'more',
            disabled,
            (el) => openPanel('more', el),
            IconMenu(),
          )
        : null,
      onAddColumn
        ? createElement(AddColumnButton, {
            disabled,
            onBeforeOpen: onBeforeAddColumn,
            onAdd: onAddColumn,
          })
        : null,
    ),
    createElement(Menu, {
      open: panel !== null,
      anchor,
      onClose: closePanel,
      minWidth:
        panel === 'properties'
          ? 240
          : panel === 'more'
            ? 180
            : panel === 'group'
              ? 220
              : panel === 'filter'
                ? 300
                : 360,
      dataAttribute: 'cs-db-toolbar-menu',
      outsideIgnoreSelector: '[data-cs-db-column-header="true"]',
      children:
        panel === 'filter'
          ? createElement(FilterPanel, {
              columns,
              filters: draftFilters,
              onDraftChange: setDraftFilters,
              onApply: applyFilters,
              hasPendingChanges: filterPendingChanges,
            })
          : panel === 'sort'
            ? createElement(SortPanel, { columns, sorts, onSortsChange })
            : panel === 'group'
              ? createElement(GroupPanel, { columns, groupBy, onGroupByChange })
              : panel === 'properties' && tableLayout
                ? createElement(PropertiesPanel, tableLayout)
                : panel === 'properties' && boardLayout
                  ? createElement(BoardSettingsPanel, boardLayout)
                : panel === 'properties' && calendarLayout
                  ? createElement(DateColumnSettingsPanel, {
                      dateColumns: calendarLayout.dateColumns,
                      columnId: calendarLayout.calendarColumnId,
                      onColumnChange: calendarLayout.onCalendarColumnChange,
                      hint: 'Rows with a from–to range appear on each day in the range.',
                      colorColumns: calendarLayout.colorColumns,
                      colorColumnId: calendarLayout.colorColumnId,
                      onColorColumnChange: calendarLayout.onColorColumnChange,
                    })
                  : panel === 'properties' && timelineLayout
                    ? createElement(DateColumnSettingsPanel, {
                        dateColumns: timelineLayout.dateColumns,
                        columnId: timelineLayout.timelineColumnId,
                        onColumnChange: timelineLayout.onTimelineColumnChange,
                        hint: 'From–to ranges render as horizontal bars spanning each day.',
                        colorColumns: timelineLayout.colorColumns,
                        colorColumnId: timelineLayout.colorColumnId,
                        onColorColumnChange: timelineLayout.onColorColumnChange,
                      })
                    : panel === 'more' && (onImportCsv || onExport)
                  ? createElement(MorePanel, {
                      onImportCsv,
                      onExport,
                      onClose: closePanel,
                      disabled: importExportDisabled,
                      exportBusy,
                    })
                  : null,
    }),
  );
}
